import {
  calculationFilename,
  saveCalculationFile,
} from './calculationDownload';

// Labels describe confirmed server observations, never elapsed time or an accepted POST alone.
export function calculationRunLabel(run) {
  if (run.status === 'succeeded') return 'Completed';
  if (run.status === 'failed') return 'Failed';
  if (run.progress?.phase === 'generating_report') return 'Generating report';
  if (run.status === 'ready') return 'Saving results';
  if (run.status === 'preparing' || run.progress?.phase === 'preparing')
    return 'Preparing';
  if (run.progress?.phase === 'snapshotting') return 'Preparing inputs';
  if (run.progress?.phase === 'saving_results') return 'Saving results';
  if (run.status === 'running') return 'Calculating';
  if (run.dagsterStatus === 'QUEUED') return 'Queued';
  if (run.dagsterStatus === 'STARTING') return 'Starting';
  if (run.dagsterStatus === 'STARTED') return 'Calculating';
  return 'Requested';
}

export function calculationRunSnapshot(run) {
  const workerObservedAt = Date.parse(run.dagsterObservedAt);
  const stale =
    ['requested', 'running'].includes(run.status) &&
    Number.isFinite(workerObservedAt) &&
    Date.now() - workerObservedAt > 60000;
  return {
    label: calculationRunLabel(run),
    stale,
    runId: run.runId,
    observedAt: Date.now(),
    dagsterStatusAt: run.dagsterStatusAt || null,
    dagsterObservedAt: run.dagsterObservedAt || null,
    runStatus: run.status,
    progress: run.progress || null,
    percent:
      run.status === 'succeeded'
        ? 100
        : typeof run.progress?.percent === 'number' &&
          Number.isFinite(run.progress.percent)
        ? Math.min(99, Math.max(0, run.progress.percent))
        : null,
  };
}

// One monitor owns one account/project scope. Persist only an accepted run ID;
// timestamps from unrelated successful calculations cannot complete this request.
export function createLcaRunMonitor({
  accountId,
  projectId,
  hostname,
  fetch: request = fetch,
  storage,
  getLatestToken,
  onBusy,
  notify,
  refresh,
  onStatus = () => {},
  kind = 'lca',
  purpose = 'calculation',
  onFile = saveCalculationFile,
  maxWaitMs = 90 * 60 * 1000,
}) {
  const isExport = purpose === 'export';
  const key = `${kind}-${
    isExport ? 'export' : 'run'
  }:${accountId}:${projectId}`;
  const endpoint = isExport
    ? 'intermediateCalculations'
    : kind === 'circularity'
    ? 'circularity-calculation'
    : 'lca-supply';
  const title = kind === 'circularity' ? 'Circularity' : 'LCA';
  let lastStatus = null;
  let watchGeneration = 0;
  const observed = (run) => {
    const next = calculationRunSnapshot(run);
    if (
      run.status === 'failed' &&
      next.percent === null &&
      lastStatus?.runId === run.runId
    ) {
      next.percent = lastStatus.percent;
    }
    lastStatus = next;
    onStatus(lastStatus);
  };
  const unavailable = () => {
    lastStatus = {
      ...(lastStatus || { label: 'Status unavailable' }),
      stale: true,
    };
    onStatus(lastStatus);
  };
  let disposed = false,
    busy = false,
    timer,
    deadlineTimer,
    controller,
    fileController;
  let completedRun = null,
    receivedFile = null;
  const read = () => {
    try {
      return JSON.parse(storage.getItem(key));
    } catch {
      return null;
    }
  };
  const save = (value) => {
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch {
      /* Storage may be disabled. */
    }
  };
  const remove = () => {
    try {
      storage.removeItem(key);
    } catch {
      /* Storage may be disabled. */
    }
  };
  const stop = () => {
    watchGeneration++;
    clearTimeout(timer);
    clearTimeout(deadlineTimer);
    controller?.abort();
    fileController?.abort();
    busy = false;
  };
  const finish = (message, type, terminal = true) => {
    if (disposed) return;
    stop();
    if (terminal) remove();
    onBusy(false);
    if (type === 'warning') unavailable();
    notify(message, type);
    if (type === 'success') refresh();
  };
  const http = async (url, method = 'GET') => {
    controller = new AbortController();
    const timeout = setTimeout(() => controller?.abort(), 30000);
    try {
      const response = await request(url, {
        method,
        signal: controller.signal,
        headers: {
          authorization: `Bearer ${getLatestToken()}`,
          'Content-type': 'application/json',
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(
          data?.error || data?.message || `HTTP ${response.status}`
        );
        error.status = response.status;
        throw error;
      }
      return data;
    } finally {
      clearTimeout(timeout);
    }
  };
  const complete = (run) => {
    if (isExport && !receivedFile) {
      completedRun = run;
      lastStatus = {
        ...calculationRunSnapshot(run),
        label: 'Downloading file',
        percent: null,
      };
      onStatus(lastStatus);
      return;
    }
    if (isExport) {
      getLatestToken();
      if (disposed || !busy) return;
      onFile(receivedFile.blob, receivedFile.filename);
    }
    observed(run);
    finish(
      isExport
        ? `${title} results downloaded successfully`
        : 'Successfully calculated',
      'success'
    );
  };
  const download = async (run, generation) => {
    fileController = new AbortController();
    try {
      const response = await request(
        `${hostname}/intermediateCalculations/download/${run.runId}`,
        {
          headers: { authorization: `Bearer ${getLatestToken()}` },
          signal: fileController.signal,
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const error = new Error(
          data.message || data.error || `HTTP ${response.status}`
        );
        error.status = response.status;
        throw error;
      }
      const buffer = await response.arrayBuffer();
      if (disposed || !busy || generation !== watchGeneration) return;
      if (!buffer.byteLength)
        throw new Error('The server returned an empty file');
      receivedFile = {
        blob: new Blob([buffer], {
          type:
            response.headers?.get('content-type') || 'application/octet-stream',
        }),
        filename: calculationFilename(response, kind),
      };
      if (completedRun) complete(completedRun);
    } catch (error) {
      if (disposed || !busy || generation !== watchGeneration) return;
      if (error.status === 410) {
        const message = 'Export expired. Start a new download.';
        lastStatus = {
          label: message,
          runStatus: 'failed',
          percent: null,
          stale: false,
        };
        onStatus(lastStatus);
        finish(message, 'error');
        return;
      }
      finish(
        `Download interrupted: ${error.message}. Reopen this project to retry the same export.`,
        'warning',
        false
      );
    }
  };
  const watch = (run) => {
    if (disposed) return;
    const generation = ++watchGeneration;
    receivedFile = null;
    completedRun = null;
    busy = true;
    onBusy(true);
    deadlineTimer = setTimeout(
      () =>
        finish(
          'Calculation status could not be confirmed in time. Check Calculation logs or reopen this project to resume monitoring.',
          'warning',
          false
        ),
      Math.max(0, run.deadline - Date.now())
    );
    const poll = async () => {
      try {
        const data = await http(`${hostname}/${endpoint}/runs/${run.runId}`);
        if (disposed || !busy || generation !== watchGeneration) return;
        if (
          data.runId === run.runId &&
          (!data.kind || data.kind === kind) &&
          (!data.purpose || data.purpose === purpose)
        ) {
          if (data.status === 'succeeded') {
            complete(data);
            return;
          }
          observed(data);
          if (data.status === 'failed') {
            finish(
              `Calculation failed: ${data.error || 'Unknown error'}`,
              'error'
            );
            return;
          }
        } else {
          unavailable();
        }
      } catch {
        if (!disposed && busy && generation === watchGeneration) unavailable();
      }
      if (!disposed && busy && generation === watchGeneration)
        timer = setTimeout(poll, 3000);
    };
    poll();
    if (isExport) download(run, generation);
  };
  const attach = (data) => {
    const run = { runId: data.runId, deadline: Date.now() + maxWaitMs };
    save(run);
    observed(data);
    notify(
      `${title} ${
        isExport ? 'export' : 'calculation'
      } accepted. Monitoring its progress.`,
      'info'
    );
    watch(run);
  };
  // The server reserves the run before preparing its backup. A dropped POST
  // response can therefore be reconciled without creating another calculation.
  const recoverUnknownRun = () => {
    deadlineTimer = setTimeout(
      () =>
        finish(
          'The calculation request could not be confirmed. Check Calculation logs before retrying.',
          'warning'
        ),
      30000
    );
    const probe = async () => {
      try {
        const data = await http(
          `${hostname}/${endpoint}/active-run/${
            isExport ? kind + '/' : ''
          }${projectId}`
        );
        if (disposed || !busy) return;
        if (
          typeof data?.runId === 'string' &&
          /^\d+$/.test(data.runId) &&
          (!data.kind || data.kind === kind) &&
          (!data.purpose || data.purpose === purpose) &&
          ['preparing', 'requested', 'running', 'ready'].includes(data.status)
        ) {
          clearTimeout(deadlineTimer);
          attach(data);
          return;
        }
      } catch {
        /* Reservation or connectivity may still be in progress. */
      }
      if (!disposed && busy) timer = setTimeout(probe, 3000);
    };
    probe();
  };
  const resume = () => {
    const run = read();
    if (
      busy ||
      disposed ||
      !run ||
      !/^\d+$/.test(run.runId) ||
      typeof run.runId !== 'string'
    )
      return false;
    // Reopening explicitly resumes observation for another bounded window.
    lastStatus = { label: 'Checking status', stale: false };
    onStatus(lastStatus);
    watch({ ...run, deadline: Date.now() + maxWaitMs });
    return true;
  };
  const start = async () => {
    if (disposed || busy || !accountId || !projectId) return;
    if (resume()) return;
    busy = true;
    onBusy(true);
    receivedFile = null;
    completedRun = null;
    lastStatus = { label: 'Submitting request', stale: false };
    onStatus(lastStatus);
    try {
      const data = await http(
        `${hostname}/${endpoint}/runs/${
          isExport ? kind + '/' : ''
        }${projectId}`,
        'POST'
      );
      if (disposed) return;
      if (
        typeof data.runId !== 'string' ||
        !/^\d+$/.test(data.runId) ||
        data.status !== 'requested' ||
        (data.kind && data.kind !== kind) ||
        (data.purpose && data.purpose !== purpose)
      ) {
        throw new Error(
          'The server did not return an accepted calculation run. Check Calculation logs before retrying.'
        );
      }
      attach(data);
    } catch (error) {
      if (!disposed) {
        if (
          error.name === 'AbortError' ||
          error instanceof TypeError ||
          [408, 409, 429].includes(error.status) ||
          error.status >= 500
        ) {
          recoverUnknownRun();
        } else {
          lastStatus = { label: 'Request rejected', stale: false };
          onStatus(lastStatus);
          finish(`Error starting calculation: ${error.message}`, 'error');
        }
      }
    }
  };
  return {
    start,
    resume,
    dispose: () => {
      disposed = true;
      stop();
    },
  };
}
